import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PaziaInput, PaziaTextarea, PaziaLoader, PaziaFormButton, PaziaFileDropzone } from "components/atoms";
import { coursegroupsApi, CourseGroupApi } from "api/coursegroups";
import { useToaster } from "utils";

const schema = yup.object({
  ogDescription: yup.string().optional().default(""),
  ogImageAlt: yup.string().optional().default(""),
  mainImage: yup.mixed().optional(),
});

type SeoFormValues = yup.InferType<typeof schema>;

const SeoCourseGroupRow = ({ courseGroup }: { courseGroup: CourseGroupApi }) => {
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ogDescription: courseGroup.ogDescription ?? "",
      ogImageAlt: courseGroup.ogImageAlt ?? "",
      mainImage: courseGroup.mainImage ?? "",
    },
  });
  const { handleSubmit } = methods;

  const onSubmit = async (data: SeoFormValues) => {
    setIsSaving(true);
    try {
      await coursegroupsApi.update(courseGroup.id, {
        name: courseGroup.name,
        categoryId: courseGroup.category.id,
        ogDescription: (data.ogDescription as string) || undefined,
        ogImageAlt: (data.ogImageAlt as string) || undefined,
        mainImage: data.mainImage ?? courseGroup.mainImage,
      });
      showSuccess(`Zapisano SEO dla "${courseGroup.name}"`);
    } catch (error) {
      showError(`Nie udało się zapisać SEO dla "${courseGroup.name}"`);
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"
      >
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
            {courseGroup.name}
          </p>
          <span className="shrink-0 text-xs text-slate-400">{courseGroup.category?.name}</span>
        </div>

        <div className="flex gap-3">
          {/* Image column — compact */}
          <div className="w-28 shrink-0">
            <div className="[&_.pazia-file-dropzone-mainImage]:h-full [&_img]:!max-h-20 [&_img]:!w-auto [&_img]:mx-auto">
              <PaziaFileDropzone name="mainImage" label="OG Image" type="image" className="text-xs" />
            </div>
          </div>

          {/* Fields column */}
          <div className="flex-1 grid gap-2">
            <PaziaTextarea name="ogDescription" label="OG Description" rows={2} />
            <PaziaInput name="ogImageAlt" label="OG Image Alt" />
          </div>

          {/* Save button column */}
          <div className="flex items-end shrink-0">
            <PaziaFormButton disabled={isSaving} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export const ShopSeoCourseGroupsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [courseGroups, setCourseGroups] = useState<CourseGroupApi[]>([]);

  useEffect(() => {
    coursegroupsApi
      .fetch(0, 500)
      .then((res) => setCourseGroups(res.data.data))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <PaziaLoader position="center" />;

  if (courseGroups.length === 0) {
    return <p className="text-slate-500">Brak grup kursów do edycji.</p>;
  }

  return (
    <div className="grid gap-3">
      {courseGroups.map((cg) => (
        <SeoCourseGroupRow key={cg.id} courseGroup={cg} />
      ))}
    </div>
  );
};
